import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { DATE_FORMAT, DomUtil } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import { CheckCircleThinIcon, Clock3Icon } from '@root/shared/ui-kit'

import BG_STRIPES from '../assets/bg-stripes.inline.svg'

import type { SupportTicket } from '../model/support-ticket'

type SupportTicketsItemProps = {
  ticket: SupportTicket
  onSelect: () => void
}

export const SupportTicketsItem: React.FC<SupportTicketsItemProps> = ({
  onSelect,
  ticket,
}) => {
  const { t } = useTranslation()
  const { lastMessagePreview, createdAt, subject } = ticket
  const [unreadCount] = useAtom(ticket.unreadCount)

  const [status] = useAtom(ticket.status)
  const Icon = status === 'active' ? OpenTicketIcon : ClosedTicketIcon

  return (
    <Root onClick={onSelect} data-status={status}>
      <Heading>
        <Group>
          <Icon size={12} />
          <Title ellipsis>{subject}</Title>
          {unreadCount > 0 ? (
            <UnreadMessages>+{unreadCount}</UnreadMessages>
          ) : undefined}
        </Group>
        <DateTime>
          {t('support.ticket-created-label')}{' '}
          {DateF.format(createdAt, DATE_FORMAT.dd_MM_yy)}{' '}
          <span>{DateF.format(createdAt, DATE_FORMAT.HH_mm)}</span>
        </DateTime>
      </Heading>
      <Description>{lastMessagePreview}</Description>
    </Root>
  )
}

const Root = styled.div`
  background: rgba(var(--background-10-rgb), 0.03);

  min-height: 56px;
  border-radius: 10px;
  padding: 8px;

  display: grid;
  gap: 8px;
  cursor: pointer;

  transition: background 0.15s ease-in-out;

  &[data-status='active'] {
    background-image: ${DomUtil.cssUrl(BG_STRIPES)};
    border: 1px solid var(--background-2);
  }
  &:hover {
    background: rgba(var(--background-10-rgb), 0.07);
  }
`

const Group = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  gap: 4px;
  justify-items: start;
  justify-content: start;
`

const Title = styled(Typography)`
  font-size: 12px;
  font-weight: 600;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Heading = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 4px;
`

const Description = styled.div`
  color: var(--text-11);

  font-size: 12px;
  font-weight: 500;
`

const DateTime = styled.div`
  color: var(--text-10);

  text-align: right;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;

  display: grid;
  grid-template-columns: auto auto;

  gap: 10px;
`

const OpenTicketIcon = styled(Clock3Icon)`
  color: var(--amber-9);
`

const ClosedTicketIcon = styled(CheckCircleThinIcon)`
  color: var(--green-9);
`

const UnreadMessages = styled.div`
  color: var(--appearance-1);
  background: var(--success-9);

  border-radius: 3px;
  text-align: center;
  font-size: 8px;
  font-weight: 700;
  padding-inline: 2px 4px;
  display: grid;
  height: 12px;
  align-items: center;
`
